/**
 * hook for synchronizing table of contents scroll with current page
 * handles auto-scrolling to active entry and detecting user scroll interactions
 */
import { useCallback, useEffect, useRef, useState } from "react";

import type { RefObject } from "react";
import type { TocEntry } from "../../metadoc.types";

const EARLY_ENTRY_THRESHOLD = 3; // First N entries that should scroll to top
const SCROLL_POSITION_TOLERANCE = 5; // Pixels tolerance for "at top" check
const AUTO_SCROLL_RESET_DELAY = 100; // ms to reset auto-scroll flag
const USER_SCROLL_DEBOUNCE = 500; // ms to wait after user stops scrolling

type UseTocScrollSyncOptions = {
  currentPage: number;
  activeEntry: TocEntry | undefined;
  filteredEntries: TocEntry[];
  enabled?: boolean;
};

type UseTocScrollSyncReturn = {
  listContainerRef: RefObject<HTMLDivElement>;
  hasOverflow: boolean;
};

export const useTocScrollSync = ({
  currentPage,
  activeEntry,
  filteredEntries,
  enabled = true,
}: UseTocScrollSyncOptions): UseTocScrollSyncReturn => {
  const listContainerRef = useRef<HTMLDivElement>(null);
  const filteredEntriesRef = useRef(filteredEntries);
  filteredEntriesRef.current = filteredEntries;
  const isUserScrollingRef = useRef(false);
  const isAutoScrollingRef = useRef(false);
  const userScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollEffectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldStickToBottomRef = useRef(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const lastScrollHeightRef = useRef(0);
  const stableHeightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [hasOverflow, setHasOverflow] = useState(false);

  // helper: check if container has overflow
  const checkOverflow = useCallback(() => {
    if (listContainerRef.current) {
      const hasScrollbar =
        listContainerRef.current.scrollHeight >
        listContainerRef.current.clientHeight;
      setHasOverflow(hasScrollbar);
    }
  }, []);

  // helper: find active entry DOM element
  const findActiveElement = useCallback(
    (entryId: string): HTMLElement | null => {
      if (!listContainerRef.current) return null;
      return listContainerRef.current.querySelector(
        `[data-entry-id="${entryId}"]`,
      ) as HTMLElement;
    },
    [],
  );

  // helper: calculate element visibility and position
  const calculateVisibility = useCallback(
    (element: HTMLElement, container: HTMLDivElement) => {
      const containerTop = container.scrollTop;
      const containerBottom = containerTop + container.clientHeight;
      const elementTop = element.offsetTop;
      const elementBottom = elementTop + element.offsetHeight;

      const isAtTop =
        Math.abs(elementTop - containerTop) < SCROLL_POSITION_TOLERANCE;
      const isFullyVisible =
        elementTop >= containerTop && elementBottom <= containerBottom;

      return {
        containerTop,
        containerBottom,
        elementTop,
        elementBottom,
        isAtTop,
        isFullyVisible,
      };
    },
    [],
  );

  // helper: determine if entry is one of the first few entries
  const isEarlyEntry = useCallback((entryId: string): boolean => {
    const entryIndex = filteredEntriesRef.current.findIndex(
      (e) => e.id === entryId,
    );
    return entryIndex < EARLY_ENTRY_THRESHOLD;
  }, []);

  // helper: determine if scroll is needed
  const shouldScrollToEntry = useCallback(
    (
      element: HTMLElement,
      container: HTMLDivElement,
      entryId: string,
    ): boolean => {
      const visibility = calculateVisibility(element, container);
      const isEarly = isEarlyEntry(entryId);

      // scroll if:
      // 1. element is not at the top or not fully visible, OR
      // 2. it's an early entry and container is scrolled down (force scroll to top for early pages)
      return (
        !visibility.isAtTop ||
        !visibility.isFullyVisible ||
        (isEarly && container.scrollTop > 0)
      );
    },
    [calculateVisibility, isEarlyEntry],
  );

  // helper: scroll entry into view
  const scrollToEntry = useCallback((element: HTMLElement, entryId: string) => {
    if (!listContainerRef.current) return;

    isAutoScrollingRef.current = true;
    const container = listContainerRef.current;

    // check if this entry is near the end of the list
    const entries = filteredEntriesRef.current;
    const entryIndex = entries.findIndex((e) => e.id === entryId);
    const totalEntries = entries.length;
    const isNearEnd = entryIndex >= totalEntries - 3;

    if (isNearEnd) {
      // for entries near the end, enable sticky bottom behavior
      shouldStickToBottomRef.current = true;
      lastScrollHeightRef.current = container.scrollHeight;

      // scroll to bottom
      const maxScroll = container.scrollHeight - container.clientHeight;
      container.scrollTop = maxScroll;

      // sticky mode will be disabled by ResizeObserver when height stabilizes
    } else {
      // for middle entries, scroll into view normally
      element.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }

    // reset auto-scrolling flag after a short delay
    autoScrollTimeoutRef.current = setTimeout(() => {
      isAutoScrollingRef.current = false;
    }, AUTO_SCROLL_RESET_DELAY);
  }, []);

  // effect: detect TOC overflow and handle sticky bottom
  useEffect(() => {
    if (!listContainerRef.current) return;

    const container = listContainerRef.current;

    // check on mount and when entries change (use setTimeout to ensure DOM has updated)
    const timeoutId = setTimeout(checkOverflow, 0);

    // use ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();

      const currentScrollHeight = container.scrollHeight;
      const heightChanged = currentScrollHeight !== lastScrollHeightRef.current;

      // if we're in sticky bottom mode, keep scrolling to bottom as container grows
      if (shouldStickToBottomRef.current) {
        const maxScroll = container.scrollHeight - container.clientHeight;
        container.scrollTop = maxScroll;

        // if height changed, reset the stability timer
        if (heightChanged) {
          lastScrollHeightRef.current = currentScrollHeight;

          // clear existing timeout
          if (stableHeightTimeoutRef.current) {
            clearTimeout(stableHeightTimeoutRef.current);
          }

          // disable sticky mode after height has been stable for 500ms
          stableHeightTimeoutRef.current = setTimeout(() => {
            shouldStickToBottomRef.current = false;
          }, 500);
        }
      }
    });
    resizeObserver.observe(container);
    resizeObserverRef.current = resizeObserver;

    // also check on window resize
    window.addEventListener("resize", checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      resizeObserverRef.current = null;
      window.removeEventListener("resize", checkOverflow);
    };
  }, [filteredEntries, checkOverflow]);

  // effect: scroll active entry into view when currentPage changes
  useEffect(() => {
    if (
      !listContainerRef.current ||
      !hasOverflow ||
      !activeEntry ||
      isUserScrollingRef.current ||
      !enabled
    ) {
      return;
    }

    // cancel any pending scroll effect timeout
    if (scrollEffectTimeoutRef.current) {
      clearTimeout(scrollEffectTimeoutRef.current);
    }

    // cancel any pending auto-scroll reset timeout (allow new scrolls to interrupt previous ones)
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
    }

    // use setTimeout to ensure DOM has updated before measuring/scrolling
    scrollEffectTimeoutRef.current = setTimeout(() => {
      if (!listContainerRef.current) {
        return;
      }

      const activeElement = findActiveElement(activeEntry.id);
      if (!activeElement) {
        return;
      }

      const container = listContainerRef.current;
      if (shouldScrollToEntry(activeElement, container, activeEntry.id)) {
        scrollToEntry(activeElement, activeEntry.id);
      }
    }, 0);

    return () => {
      if (scrollEffectTimeoutRef.current) {
        clearTimeout(scrollEffectTimeoutRef.current);
      }
    };
  }, [
    currentPage,
    activeEntry?.id,
    hasOverflow,
    enabled,
    findActiveElement,
    shouldScrollToEntry,
    scrollToEntry,
  ]);

  // effect: handle user scrolling - temporarily disable auto-scroll
  useEffect(() => {
    const container = listContainerRef.current;
    if (!container || !hasOverflow) return;

    const handleScroll = () => {
      // if we're auto-scrolling, ignore this event
      if (isAutoScrollingRef.current) {
        return;
      }

      // user is manually scrolling
      isUserScrollingRef.current = true;

      // clear existing timeout
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }

      // re-enable auto-scroll after user stops scrolling
      userScrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, USER_SCROLL_DEBOUNCE);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, [hasOverflow]);

  return {
    listContainerRef,
    hasOverflow,
  };
};
